/* eslint-disable @typescript-eslint/no-explicit-any */
import { OrderByInput } from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { InfiniteDataGrid } from './infiniteDataGrid';

export type AssignmentListGridProps = {
  /** Graphback model name (drives the react-query cache key). */
  modelName: string;
  /** GraphQL `findAssignable…` query for this entity. */
  query: any;
  /** Dot path to the list payload in the response (e.g. `group.findAssignableAssets`). */
  dataPrefix: string;
  /** Checkbox + label columns for this entity. */
  columns: ColumnDef<any, any>[];
  /** Fully-built server filter (includes the search keyword and any entity ids). */
  filter: any;
  /** Authoritative selection, owned by the parent form and diffed on save. */
  assignments: string[];
  /** Receives the full next selection on every toggle. */
  onAssignmentsChange: (ids: string[]) => void;
  maxHeight?: number;
  textEmpty?: string | ReactNode;
  orderBy?: OrderByInput;
  pageSize?: number;
  /** Normalise a row to its stable id (default `row.id`); e.g. `String(row.id)`. */
  getRowId?: (row: any) => string;
  /** Display-only checked overlay for rows assigned via another path (e.g. group). */
  isRowForceSelected?: (row: any) => boolean;
};

/**
 * Shared searchable, checkbox-select list used by every "assignment editor"
 * modal/panel (person↔assets, group↔members, product↔addons, routine↔assets,
 * feature assignments, …).
 *
 * Selection is tracked by row id via `InfiniteDataGrid`'s id-selection mode, so
 * selections survive server-side search, sorting and pagination. This is the
 * one place the previous per-editor bug lived — the grid rebuilt selection from
 * only the visible rows and replaced the parent's list, so searching narrowed
 * the results and silently unassigned everything not matching the term on save
 * (GRIPP #2952 follow-up). Each editor supplies its own query/columns/filter
 * and delegates the selection mechanics here.
 */
export const AssignmentListGrid = ({
  modelName,
  query,
  dataPrefix,
  columns,
  filter,
  assignments,
  onAssignmentsChange,
  maxHeight,
  textEmpty,
  orderBy,
  pageSize = 100,
  getRowId,
  isRowForceSelected,
}: AssignmentListGridProps) => {
  return (
    <InfiniteDataGrid
      modelName={modelName}
      hideResultsLabel={true}
      query={query}
      filter={filter}
      pageSize={pageSize}
      columns={columns}
      listMaxHeight={maxHeight as number}
      containerStyle={{ height: maxHeight }}
      paddingBottom={0}
      textEmpty={textEmpty}
      dataPrefix={dataPrefix}
      orderBy={orderBy}
      selectedRowIds={assignments}
      onSelectedRowIdsChange={onAssignmentsChange}
      getRowId={getRowId}
      isRowForceSelected={isRowForceSelected}
      enableScrollRefresh={true}
      showScrollIndicator={true}
    />
  );
};
